import './EditCalendar.css'
import { ErrorAlert } from '../../common/ErrorAlert';
import { useState, useMemo } from "react";
import { Button, Checkbox, Divider, Form, Input, Select, Spin, Steps, TimePicker, Tooltip, InputNumber } from "antd";
import { mapToCalendar } from '../../../models/calendar/Event.model';
import { useConfig } from '../../../contexts/ConfigContext';
import { Calendar, CalendarCategory, WeekAvailable } from '../../../models/calendar/Calendar.model';
import { useCompany } from '../../../contexts/CompanyContext';
import { CalendarService } from '../../../services/calendar.api.client';
import { useCalendars } from '../../../contexts/calendar/CalendarsContext.context';
import { useUpdater } from '../../../contexts/calendar/UpdateCalendarSelect.context';
import { Dayjs } from 'dayjs';
import dayjs from '../../../utils/dayjs'
import { typeOptions as typeOptionsData } from '../../../constants/eventConstants';
import { CopyOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useUser } from '../../../contexts/UserContext';
import { useModalCalendar } from '../../../contexts/calendar/ModalCalendarContext.context';


type WorkHours = {
  day: string;
  start: Dayjs;
  end: Dayjs;
  enabled: boolean;
};

type FieldType = {
    name: string;
    description: string;
    uidCalendar: string;
    uidCompany: string;
    category: CalendarCategory;
    customEvents?: { id?: string; name: string; duration: number }[];
};

export const CreateCalendar = ()=>{
    const {company} = useCompany();
    const {setCalendars} = useCalendars();
    const {setOptions: setOptionsModal} = useModalCalendar();
    const {user} = useUser();
    const [errorText,setErrorText] = useState<string>('');
    const [statusForm,setStatusForm] = useState<string>('SUCCESS');
    const [form] = Form.useForm<FieldType>();
    const {config} = useConfig();
    const {setUpdater} = useUpdater();
    const [currentStep,setCurrentStep] = useState<number>(0);
    const [info1Step,setInfo1Step] = useState<Partial<Calendar>>({} as Calendar);
    const startTime = dayjs('08:00', 'HH:mm');
    const endTime = dayjs('23:00', 'HH:mm');
    const [workHours, setWorkHours] = useState<WorkHours[]>([
        { day: "Lunes", start: startTime, end: endTime, enabled: true },
        { day: "Martes", start: startTime, end: endTime, enabled: true },
        { day: "Miércoles", start: startTime, end: endTime, enabled: true },
        { day: "Jueves", start: startTime, end: endTime, enabled: true },
        { day: "Viernes", start: startTime, end: endTime, enabled: true },
        { day: "Sábado", start: startTime, end: endTime, enabled: false },
        { day: "Domingo", start: startTime, end: endTime, enabled: false },
    ]);

    const handleWorkHoursChange = (index: number, field: "start" | "end", value: Dayjs) => {
    const updated = [...workHours];
    updated[index][field] = value;
    setWorkHours(updated);
    };

    // Generate dynamic type options from CompanyContext
    const dynamicTypeOptions = useMemo(() => {
        let opts = company.moduleOptions?.filter((opt: any) => opt.type === 'EVENT_TYPE' && opt.show).map((opt: any) => ({
            label: opt.string_value,
            value: opt.key
        })) || [];
        
        // Fallback to constants if no dynamic options found
        if (opts.length === 0) {
            opts = typeOptionsData;
        }
        return opts;
    }, [company.moduleOptions]);

    const handleOneStep = (dataForm:FieldType) =>{
        const data:Record<string, any> = {
            uidCalendar: dataForm.uidCalendar,
            uidCompany: dataForm.uidCompany,
            description: dataForm.description,
            name: dataForm.name,
            category: dataForm.category || 'GENERIC',
            customEvents: dataForm.customEvents?.map(ev => ({
                ...ev,
                id: ev.id || Math.random().toString(36).substring(7)
            })) || []
        };
        const calendarObj = mapToCalendar(data);
        calendarObj.customEvents = data.customEvents;
        calendarObj.category = data.category;
        setInfo1Step(calendarObj);
        setCurrentStep(1);
    }
    const handleSubmit = () => {
        if(config.key == null || config.key == ''){
            setErrorText('No se ha podido obtener la clave de seguridad, intente nuevamente.');
            return;
        }
        setStatusForm('LOAD');
        const calendarObj = info1Step;
        const week = workHours.filter((wh)=>wh.enabled).map((wh)=>{
          const response:WeekAvailable = {
            numberDay: wh.day === "Lunes" ? 0 : wh.day === "Martes" ? 1 : wh.day === "Miércoles" ? 2 : wh.day === "Jueves" ? 3 : wh.day === "Viernes" ? 4 : wh.day === "Sábado" ? 5 : wh.day === "Domingo" ? 6 : -1,
            start: {
              hour: wh.start.hour(),
              minute: wh.start.minute(),
              period: wh.start.hour() >= 12 ? "PM" : "AM"
            },
            end: {
              hour: wh.end.hour(),
              minute: wh.end.minute(),
              period: wh.end.hour() >= 12 ? 'PM' : 'AM'
            }
          }
          return response;
        });
        calendarObj.weekAvailable = week;
        calendarObj.users = [user.id]; // Store as number
        calendarObj.createdBy = user.uid;
        calendarObj.company_id = company.current.id; // Numeric ID
        calendarObj.created_by_id = user.id; // Correctly mapped to Numeric ID
        
        // Pass enriched creator data for logic-free driver and enriched Firestore
        const enrichedData = {
            ...calendarObj,
            created_by_id: user.id, // Ensure it's at the top level for the driver
            category: calendarObj.category || 'GENERIC',
            creator_enriched: {
                id: user.id,
                uid: user.uid,
                name: user.name
            },
            users_enriched: [
                {
                    id: user.id,
                    uid: user.uid,
                    name: user.name
                }
            ]
        };

        CalendarService.createCalendar(enrichedData, company.current, config.key).then((response)=>{
            const data: Calendar = response.calendar || response;
            setCalendars((prevCalendars:Calendar[])=> [...prevCalendars, data]);
            setStatusForm('SUCCESS');
            setUpdater((prev)=>!prev);
            
            // Close modal and reset form
            setTimeout(() => {
                setOptionsModal((prev) => ({
                    ...prev,
                    isopen: false,
                    isCreating: false
                }));
                form.resetFields();
                setCurrentStep(0);
                setStatusForm('SUCCESS'); // Keep as success for a brief moment or reset if needed
            }, 1000);
        }).catch((error_)=>{
            //console.log(error_)
            setStatusForm('SUCCESS');
            setErrorText(error_.message);
        })
    };

    switch(statusForm){
        case 'LOAD':
            return(
                <div className="w-[100%] container-load">
                     <Spin size="large" />
                </div>
            );

        default:
            return (
                <>
                    <div style={{padding:'0 16px'}}>
                        <ErrorAlert message={errorText} onDismiss={() => setErrorText('')} />
                    </div>
                    <Steps
                        style={{padding:'0 16px'}}
                        current={currentStep}
                        items={[
                        {
                            title: 'Información',
                        },
                        {
                            title: 'Horarios',
                        },
                        ]}
                    />
                    <article className='p-5 mt-2'>
                        {
                            currentStep == 0 ?
                    <Form className="edit-form pr-5 pl-5 pb-2" onFinish={handleOneStep} form={form} layout="vertical"
                                    initialValues={{
                                        uidCompany: company.current.uid,
                                        name: '',
                                        description: '',
                                        category: 'GENERIC',
                                    }}>

                                    <Form.Item label={null} hidden={true} name="uidCompany">
                                        <input type="hidden" name="uidCompany"/>
                                    </Form.Item>

                                    <Form.Item<FieldType>
                                        label="Tipo de Calendario (Eventos que alojará)"
                                        name="category"
                                        rules={[{ required: true, message: 'Seleccione un tipo de evento para este calendario!' }]}
                                        className="space-y-2 mb-2 w-[100%]"
                                        tooltip="Define el tipo de eventos que se manejarán en este calendario. Si eliges Genérico, podrás crear eventos de múltiples tipos."
                                    >
                                        <Select placeholder="Selecciona un tipo">
                                            <Select.Option key="GENERIC" value="GENERIC">Genérico (Sin restricción)</Select.Option>
                                            {dynamicTypeOptions.map((option: any) => (
                                                <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                        
                                    <Form.Item<FieldType>
                                        label="Nombre"
                                        name="name"
                                        rules={[{ required: true, message: 'Ingrese un nombre para el calendario!' }]}
                                        className="space-y-2 mb-2 w-[100%]"
                                    >
                                        <Input type="text" name="name"/>
                                    </Form.Item>
                                    <Form.Item<FieldType>
                                        label="Descripción"
                                        name="description"
                                        rules={[{ required: true, message: 'Ingrese una descripción para el calendario!' }]}
                                        className="space-y-2 mb-2 w-[100%]"
                                    >
                                        <Input type="text" name="description"/>
                                    </Form.Item>
                                    
                                    <Divider style={{ margin: '16px 0' }} orientation="left">Eventos Personalizados</Divider>
                                    <Form.List name="customEvents">
                                        {(fields, { add, remove }) => (
                                            <>
                                                {fields.map(({ key, name, ...restField }) => (
                                                    <div key={key} style={{ display: 'flex', marginBottom: 8, gap: '8px', alignItems: 'baseline', width: '100%' }}>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'id']}
                                                            hidden
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'name']}
                                                            rules={[{ required: true, message: 'Falta nombre' }]}
                                                            style={{ flex: 1, marginBottom: 0 }}
                                                        >
                                                            <Input placeholder="Nombre del evento (ej. Consulta)" />
                                                        </Form.Item>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'duration']}
                                                            rules={[{ required: true, message: 'Falta duración' }]}
                                                            style={{ width: '140px', marginBottom: 0 }}
                                                        >
                                                            <InputNumber placeholder="Duración (min)" suffix="(min)" min={1} style={{ width: '100%' }} />
                                                        </Form.Item>
                                                        <MinusCircleOutlined onClick={() => remove(name)} style={{ marginTop: '10px' }} />
                                                    </div>
                                                ))}
                                                <Form.Item>
                                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                                        Añadir evento personalizado
                                                    </Button>
                                                </Form.Item>
                                            </>
                                        )}
                                    </Form.List>

                                    <div className="flex gap-2 justify-end">
                                        <Button type="primary" htmlType="submit">Siguiente</Button>
                                    </div>
                                </Form>
                            : currentStep == 1 ?
                                <article className='px-2'>        
                                    <div className="flex flex-col gap-2" style={{ marginTop: '16px', marginBottom: '4px' }}>
                                        {workHours.map((wh, i) => (
                                            <div key={wh.day} className={`schedule-row ${wh.enabled ? 'active' : ''}`}>
                                                {/* Column 1: Checkbox + Day Name */}
                                                <div className="schedule-day-info">
                                                    <Checkbox checked={wh.enabled}
                                                        onChange={(e)=>{
                                                            setWorkHours((prev)=>{
                                                                const updated = [...prev];
                                                                updated[i].enabled = e.target.checked;
                                                                if(e.target.checked){
                                                                    updated[i].start = startTime;
                                                                    updated[i].end = endTime;
                                                                }
                                                                return updated;
                                                            })
                                                        }}
                                                    />
                                                    <span className="schedule-day-label">{wh.day}</span> 
                                                </div>

                                                {/* Column 2: Content (TimePicker or Status Text) */}
                                                <div className="schedule-content">
                                                    {wh.enabled && wh.start && wh.end ? (
                                                        <TimePicker.RangePicker 
                                                            value={[wh.start, wh.end]} 
                                                            format={'HH:mm'}
                                                            placement="bottomLeft"
                                                            onChange={(value) => {
                                                                if (value) {
                                                                    handleWorkHoursChange(i, "start", value[0]!);
                                                                    handleWorkHoursChange(i, "end", value[1]!);
                                                                }
                                                            }}
                                                            style={{ width: '100%', maxWidth: '200px' }}
                                                        />
                                                    ) : (
                                                        <span className="schedule-status-text">No laborable</span>
                                                    )}
                                                </div>

                                                {/* Column 3: Actions */}
                                                <div className="schedule-actions">
                                                    {wh.enabled ? (
                                                        <Tooltip title="Copiar horario a días activos">
                                                            <Button 
                                                                icon={<CopyOutlined />} 
                                                                type="text"
                                                                size="small" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    setWorkHours((prev) => {
                                                                        return prev.map((day) => {
                                                                            if (day.enabled) {
                                                                                return { ...day, start: wh.start, end: wh.end };
                                                                            }
                                                                            return day;
                                                                        });
                                                                    });
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Button 
                                                            icon={<PlusOutlined />} 
                                                            type="dashed"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setWorkHours((prev) => {
                                                                    const updated = [...prev];
                                                                    updated[i].enabled = true;
                                                                    updated[i].start = startTime;
                                                                    updated[i].end = endTime;
                                                                    return updated;
                                                                });
                                                            }}
                                                        >
                                                            Habilitar
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <Divider style={{ margin: '12px 0 12px 0' }} />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', paddingBottom: '8px' }}>
                                        <Button size="large" onClick={() => setCurrentStep(0)}>Anterior</Button>
                                        <Button size="large" type="primary" onClick={handleSubmit}>Guardar</Button>
                                    </div>
                                </article>
                            :
                            null
                        }
                    </article>
                </>
            )
    }
}
